import { 
  Box, 
  MenuItem, 
  TextField, 
  Typography, 
  Avatar, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip,
  Switch,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, Card, DataTable, StatusBadge, ActionButton, ConfirmDialog, TouchFriendlyTooltip } from "../shared";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Constants from "../../config/Constants";
import { apiFlagIsTrue } from "../../Utils/func";
import { formatClubType } from "../../Utils/displayLabels";

const EMPTY_CLUB = () => ({ name: "", email: "" });
const INVITE_PERMISSION_MSG = "not permitted to invite clubs";

/** ORG-code (master-initiated) connections only; CLB club-partner connections return false. */
const canControlEmailVisibility = (subOrg) =>
  subOrg?.can_control_email_visibility !== undefined
    ? apiFlagIsTrue(subOrg.can_control_email_visibility)
    : true;

const SubOrganizations = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const canInviteClubsRaw = props?.props?.profile?.can_invite_clubs;
  const canInviteClubs =
    canInviteClubsRaw == null ? true : apiFlagIsTrue(canInviteClubsRaw);
  const saveUserData = props?.props?.save_user_data;
  const [subOrgs, setSubOrgs] = useState([]);
  const [openSendInvite, setOpenSendInvite] = useState(false);
  const [inviteClubs, setInviteClubs] = useState([EMPTY_CLUB()]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, subOrg: null });
  const [editDialog, setEditDialog] = useState({ open: false, subOrg: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, subOrg: null, deleteUser: false });
  const [editForm, setEditForm] = useState({ name: "", school_type: "", id_item_logo: null, logo_url: "", allow_email_visibility: false });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [membersDialog, setMembersDialog] = useState({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false });
  const [usersDialog, setUsersDialog] = useState({ open: false, org: null, users: [] });

  const loadSubOrgs = async () => {
    if (!token) return;
    setLoading(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const data = await ApiServices.GetApiCall(
      ApiEndpoint.MASTER_ORG_SUB_ORGS,
      headers
    );
    setLoading(false);
    if (data?.status) {
      const subOrgsData = data.data || [];
      // Ensure allow_email_visibility has a default value if not present
      const subOrgsWithDefaults = subOrgsData.map(org => ({
        ...org,
        allow_email_visibility: org.allow_email_visibility !== undefined ? org.allow_email_visibility : false,
      }));
      // Debug: Log sub-org data to check members_count
      if (process.env.NODE_ENV === 'development' && subOrgsWithDefaults.length > 0) {
        console.log('Sub-orgs data from API:', subOrgsWithDefaults);
        subOrgsWithDefaults.forEach((org, index) => {
          console.log(`Sub-org ${index + 1} (${org.name}):`, {
            id: org.id,
            members_count: org.members_count,
            member_count: org.member_count,
            total_members: org.total_members,
            user_count: org.user_count,
            allow_email_visibility: org.allow_email_visibility,
          });
        });
      }
      setSubOrgs(subOrgsWithDefaults);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  useEffect(() => {
    if (userType === "master_org") {
      loadSubOrgs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, token]);

  // Fetch sub-org details
  const fetchSubOrgDetails = async (subOrgId) => {
    if (!token || !subOrgId) return null;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      const data = await ApiServices.GetApiCall(
        `${ApiEndpoint.MASTER_ORG_SUB_ORG_DETAILS}${subOrgId}`,
        headers
      );
      if (data?.status) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch sub-org details:", error);
      return null;
    }
  };

  // Handle view sub-org
  const handleView = async (subOrg) => {
    const details = await fetchSubOrgDetails(subOrg.id);
    setViewDialog({
      open: true,
      subOrg: details
        ? {
            ...subOrg,
            ...details,
            can_control_email_visibility:
              details.can_control_email_visibility ?? subOrg.can_control_email_visibility,
          }
        : subOrg,
    });
  };

  // Fetch members for a sub-org
  // eslint-disable-next-line no-unused-vars
  const fetchSubOrgMembers = async (subOrgId, subOrgName) => {
    if (!token || !subOrgId) return;
    
    setMembersDialog({ 
      open: true, 
      subOrgId, 
      subOrgName, 
      members: [], 
      loading: true 
    });
    
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    
    try {
      // Backend endpoint needed: POST /api/v3/master-org/users with sub_org_id filter
      // OR: GET /api/v3/master-org/sub-org/{id}/members (preferred)
      const requestBody = {
        type: "user",
        pagination: false, // Get all members
        sub_org_id: subOrgId, // Filter users by sub-org ID
        status: "active",
      };
      
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_USERS,
        JSON.stringify(requestBody),
        headers
      );
      
      if (data?.status) {
        const members = Array.isArray(data.data) ? data.data : [];
        const activeMembers = members.filter((m) => m.status === "active");
        // Debug: Log actual member count vs displayed count
        if (process.env.NODE_ENV === 'development') {
          console.log(`Sub-org ${subOrgName} (ID: ${subOrgId}) members:`, {
            actualCount: activeMembers.length,
            members: activeMembers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}`, email: m.email })),
          });
        }
        setMembersDialog(prev => ({ 
          ...prev, 
          members: activeMembers, 
          loading: false 
        }));
      } else {
        toast.error(data?.message || "Failed to load members");
        setMembersDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Failed to fetch sub-org members:", error);
      toast.error("Failed to load members");
      setMembersDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle edit sub-org
  const handleEdit = (subOrg) => {
    setEditForm({
      name: subOrg.name || "",
      school_type: subOrg.school_type || "",
      id_item_logo: subOrg.id_item_logo || null,
      logo_url: subOrg.logo_url || "",
      allow_email_visibility: subOrg.allow_email_visibility || false,
    });
    setEditDialog({ open: true, subOrg });
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      toast.error("Only JPG and PNG files are allowed");
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    const myHeaders = new Headers();
    myHeaders.append("x-access-token", token);
    formData.append("file", file, file.name);
    formData.append("type", "image");

    try {
      const response = await fetch(`${Constants.BASE_API_URL}user/upload`, {
        method: "POST",
        headers: myHeaders,
        body: formData,
      });
      const data = await response.json();
      
      if (data?.status && data?.data) {
        setEditForm(prev => ({
          ...prev,
          id_item_logo: data.data.id,
          logo_url: data.data.url || prev.logo_url,
        }));
        toast.success("Logo uploaded successfully");
      } else {
        toast.error("Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error", error);
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle update sub-org
  const handleUpdate = async () => {
    if (!token || !editDialog.subOrg) return;
    
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    
    const body = {
      sub_org_id: editDialog.subOrg.id,
      name: editForm.name,
      school_type: editForm.school_type,
    };

    if (canControlEmailVisibility(editDialog.subOrg)) {
      body.allow_email_visibility = editForm.allow_email_visibility;
    }
    
    if (editForm.id_item_logo) {
      body.id_item_logo = editForm.id_item_logo;
    }

    props.setIsLoaded(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_SUB_ORG_UPDATE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      
      if (data?.status) {
        toast.success(data.message || "Club updated successfully");
        setEditDialog({ open: false, subOrg: null });
        loadSubOrgs();
      } else {
        toast.error(data?.message || "Failed to update club");
      }
    } catch (error) {
      props.setIsLoaded(false);
      toast.error("Failed to update club");
    }
  };

  // Handle delete sub-org
  const handleDelete = async () => {
    if (!token || !deleteDialog.subOrg) return;
    
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    
    const body = {
      sub_org_id: deleteDialog.subOrg.id,
      delete_user: deleteDialog.deleteUser,
    };

    props.setIsLoaded(true);
    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_SUB_ORG_DELETE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      
      if (data?.status) {
        toast.success(data.message || "Connection removed successfully");
        setDeleteDialog({ open: false, subOrg: null, deleteUser: false });
        loadSubOrgs();
      } else {
        toast.error(data?.message || "Failed to delete club");
      }
    } catch (error) {
      props.setIsLoaded(false);
      toast.error("Failed to delete club");
    }
  };

  // If the API rejects the invite due to a disabled capability, persist that flag
  const handleInvitePermissionDenied = (data) => {
    const message = data?.message || "";
    if (message.toLowerCase().includes(INVITE_PERMISSION_MSG) && saveUserData) {
      saveUserData({
        user: { ...props?.props?.profile, can_invite_clubs: false },
      });
    }
  };

  const handleInviteClubChange = (index, field, value) => {
    setInviteClubs((prev) =>
      prev.map((club, i) => (i === index ? { ...club, [field]: value } : club))
    );
  };

  const handleAddInviteClub = () => setInviteClubs((prev) => [...prev, EMPTY_CLUB()]);

  const handleRemoveInviteClub = (index) =>
    setInviteClubs((prev) => prev.filter((_, i) => i !== index));

  const closeSendInvite = () => {
    setOpenSendInvite(false);
    setInviteClubs([EMPTY_CLUB()]);
  };

  const handleSendInvite = async () => {
    if (!token) return;

    const validClubs = inviteClubs.filter((c) => c.name.trim() && c.email.trim());
    if (validClubs.length === 0) {
      toast.error("Add at least one club with a name and email");
      return;
    }
    const invalidClubs = inviteClubs.filter(
      (c) => (c.name.trim() && !c.email.trim()) || (!c.name.trim() && c.email.trim())
    );
    if (invalidClubs.length > 0) {
      toast.error("Each club entry must have both a name and an email");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const emailSubject = "You've been invited to join SportsApp";
    const emailBody = `Hello,\n\nYour association has invited you to join SportsApp. Your unique code is {{CODE}} — simply enter this when registering.\n\nOnce your account is set up, you can invite your members to join your page and communicate with them via push notifications. You can also use SportsApp to find coaches, players, and parents through the "My Connect" feature, and invite new members to join your page.\n\nOnly you and your association can view your members' details — all information remains private on the platform.\n\nIf you choose not to actively use SportsApp, we recommend at least inviting your members so your association can communicate important updates to them and share key information with you via desktop.\n\nNote: This invite was sent to this email address, but you can change your email address after registering in your account settings.`;

    setSendingInvite(true);
    const results = await Promise.allSettled(
      validClubs.map((club) =>
        ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_CODES_SEND_INVITE,
          JSON.stringify({
            club_name: club.name.trim(),
            club_email: club.email.trim(),
            email_subject: emailSubject,
            email_body: emailBody,
          }),
          headers
        )
      )
    );
    setSendingInvite(false);

    const succeeded = results.filter(
      (r) => r.status === "fulfilled" && r.value?.status
    ).length;
    const failed = results.length - succeeded;

    if (succeeded > 0 && failed === 0) {
      toast.success(
        `Invite${succeeded > 1 ? "s" : ""} sent successfully to ${succeeded} club${succeeded > 1 ? "s" : ""}`
      );
    } else if (succeeded > 0) {
      toast.success(`${succeeded} invite${succeeded > 1 ? "s" : ""} sent`);
      toast.error(`${failed} invite${failed > 1 ? "s" : ""} failed`);
    } else {
      const firstError = results.find((r) => r.status === "fulfilled")?.value;
      handleInvitePermissionDenied(firstError);
      toast.error(firstError?.message || "Failed to send invites");
    }

    if (succeeded > 0) {
      closeSendInvite();
    }
  };

  // Only associations that invited clubs (ORG codes) can set per-club email export — not club partners (CLB).
  const showClubEmailVisibility = subOrgs.some(canControlEmailVisibility);

  const emailVisibilityColumn = {
    field: "allow_email_visibility",
    label: "Email Visibility",
    width: 150,
    align: "right",
    mobileSameLineGroup: "meta",
    render: (value, row) => {
      const emailVisibility = row.allow_email_visibility !== undefined ? row.allow_email_visibility : false;
      return (
        <TouchFriendlyTooltip
          title={
            emailVisibility
              ? "Club can view and export member emails"
              : "Club cannot view or export member emails"
          }
          arrow
        >
          <Chip
            label={emailVisibility ? "Enabled" : "Disabled"}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: emailVisibility
                ? isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)"
                : isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.15)",
              color: emailVisibility
                ? isDark ? "#4ade80" : "#22c55e"
                : isDark ? "#f87171" : "#ef4444",
            }}
          />
        </TouchFriendlyTooltip>
      );
    },
  };

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url)
        const logoUrl = row.logo_url || "";
        const orgName = row.name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
              {row.name || "-"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {row.user?.email || "-"}
            </Typography>
            {row.school_type && (
              <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                {formatClubType(row.school_type)}
              </Typography>
            )}
          </Box>
        </Box>
        );
      },
    },
    {
      field: "members",
      label: "Members",
      width: 200,
      mobileInlineWithTitle: true,
      render: (_, row) => {
        // Check various possible field names for user/member data
        // Backend should return user info as an ARRAY since orgs can have multiple users
        let users = row.users || 
                   row.members || 
                   row.user || 
                   row.member ||
                   [];
        
        // Handle case where it might be a single object (backward compatibility)
        if (!Array.isArray(users)) {
          // If it's a single object, convert to array
          if (users && typeof users === 'object' && Object.keys(users).length > 0) {
            users = [users];
          } else {
            users = [];
          }
        }
        
        // Remove duplicates based on user ID
        const uniqueUsers = [];
        const seenIds = new Set();
        
        users.forEach(user => {
          const status = user.status || user.user?.status;
          if (status && status !== "active") return;

          const userId = user.id || user.user_id || user.id_user;
          const userName = user.first_name || user.name || "";
          
          // Only include if it has a name and we haven't seen this ID before
          if (userName || userId) {
            if (userId && !seenIds.has(userId)) {
              seenIds.add(userId);
              uniqueUsers.push(user);
            } else if (!userId) {
              // If no ID, check by name to avoid duplicates
              const nameExists = uniqueUsers.some(u => 
                (u.first_name || u.name) === userName && (u.email || u.user?.email) === (user.email || user.user?.email)
              );
              if (!nameExists) {
                uniqueUsers.push(user);
              }
            }
          }
        });
        
        // If no user data, show "-"
        if (uniqueUsers.length === 0) {
          return (
            <Typography sx={{ color: isDark ? "#718096" : "#718096", fontSize: "0.875rem" }}>
              -
            </Typography>
          );
        }
        
        // Display avatars in a row with "+X" indicator (always show max 2 avatars, then "+X")
        const maxVisible = 2;
        const visibleUsers = uniqueUsers.slice(0, maxVisible);
        const remainingCount = uniqueUsers.length - maxVisible;
        
        return (
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setUsersDialog({ open: true, org: row, users: uniqueUsers });
            }}
          >
            {visibleUsers.map((user, index) => {
              const profileImage = user.profile_image || user.user?.profile_image || "";
              const firstName = user.first_name || user.name || "";
              const lastName = user.last_name || "";
              const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "U";
              
              return (
                <Avatar
                  key={user.id || user.user_id || index}
                  src={profileImage}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    color: isDark ? "#818cf8" : "#1976d2",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                    marginLeft: index > 0 ? "-8px" : 0,
                    zIndex: maxVisible - index,
                    "&:hover": {
                      zIndex: 10,
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  {initials}
                </Avatar>
              );
            })}
            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                  color: isDark ? "#818cf8" : "#1976d2",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  border: isDark ? "2px solid rgba(255, 255, 255, 0.1)" : "2px solid #ffffff",
                  marginLeft: "-8px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.3)" : "rgba(25, 118, 210, 0.25)",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setUsersDialog({ open: true, org: row, users: uniqueUsers });
                }}
              >
                +{remainingCount}
              </Avatar>
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      align: "left",
      mobileSameLineGroup: "meta",
      render: (value, row) => {
        const connectionStatus = value || "approved";
        const userStatus = row.user?.status || row.status || "active";
        
        // Show connection status badge
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <StatusBadge status={connectionStatus} />
            {connectionStatus === "approved" && userStatus !== "active" && (
              <Typography 
                sx={{ 
                  fontSize: '0.7rem', 
                  color: '#ef4444',
                  fontStyle: 'italic'
                }}
              >
                User: {userStatus}
              </Typography>
            )}
          </Box>
        );
      },
    },
    ...(showClubEmailVisibility ? [emailVisibilityColumn] : []),
  ];

  // Export sub-organizations to CSV
  const handleExport = async () => {
    if (!token) return;
    
    setIsExporting(true);
    
    try {
      // Export all sub-orgs without status filter
      const url = ApiEndpoint.MASTER_ORG_SUB_ORG_EXPORT;
      
      const headers = {
        "x-access-token": token,
      };

      const { blob } = await ApiServices.GetBlobApiCall(url, headers);
      
      if (!blob) {
        throw new Error('Export failed - no data returned');
      }

      // Custom filename for sub-organization export
      const dateStr = new Date().toISOString().split('T')[0];
      const customFilename = `sub-org-data-${dateStr}.csv`;

      // Check if blob is empty (CSV with only headers)
      const blobSize = blob.size;
      
      if (blobSize < 100) {
        const text = await blob.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length <= 1) {
          toast.warning('Export completed but no data found matching the filters.');
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = customFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Export successful! File: ${customFilename} (${(blobSize / 1024).toFixed(2)} KB)`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Export failed. Check console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  // Actions column
  const renderActions = (row) => (
    <Box 
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}
    >
      <ActionButton
        variant="ghost"
        size="small"
        startIcon={<VisibilityIcon />}
        onClick={(e) => {
          e.stopPropagation();
          handleView(row);
        }}
        sx={{ minWidth: "auto" }}
      >
        View
      </ActionButton>
      <ActionButton
        variant="ghost"
        size="small"
        startIcon={<ArticleIcon />}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/master-org/clubs/${row.id}/content`, {
            state: { clubName: row.name },
          });
        }}
        sx={{ minWidth: "auto" }}
      >
        Content
      </ActionButton>
      <ActionButton
        variant="ghost"
        size="small"
        startIcon={<EditIcon />}
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row);
        }}
        sx={{ minWidth: "auto" }}
      >
        Edit
      </ActionButton>
      <ActionButton
        variant="ghost"
        size="small"
        startIcon={<DeleteOutlineIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setDeleteDialog({ open: true, subOrg: row, deleteUser: false });
        }}
        sx={{ 
          minWidth: "auto",
          color: isDark ? "#ef4444" : "#dc2626",
          "&:hover": {
            backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
          },
        }}
      >
        Delete
      </ActionButton>
    </Box>
  );

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Clubs" subtitle="Access denied. This page is for club partner accounts." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Clubs"
        subtitle="Review approved clubs."
        breadcrumbs={[{ label: "Club Partner" }, { label: "Clubs" }]}
      >
        <TouchFriendlyTooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                How clubs work
              </Typography>
              <Typography variant="body2">
                • This list shows clubs that have been approved for your club partner.
              </Typography>
              <Typography variant="body2">
                • Use <strong>Invite Codes</strong> to let new clubs sign up, and <strong>Join Requests</strong> to approve them.
              </Typography>
              <Typography variant="body2">
                • Click <strong>View</strong> for full details, <strong>Members</strong> to see people in each club, or use <strong>Delete</strong> to remove a connection.
              </Typography>
            </Box>
          }
          arrow
        >
          <IconButton
            size="small"
            sx={{ color: isDark ? "#cbd5e1" : "#4a5568", mr: 1 }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </TouchFriendlyTooltip>
        {canInviteClubs ? (
          <ActionButton
            variant="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenSendInvite(true)}
            sx={{ mr: 1 }}
          >
            Invite Clubs
          </ActionButton>
        ) : (
          <TouchFriendlyTooltip
            title="Club-invite capability is disabled. Ask your admin to enable inviting clubs for your club."
            arrow
          >
            <Box component="span" sx={{ display: "inline-flex", mr: 1 }}>
              <ActionButton variant="primary" startIcon={<PersonAddIcon />} disabled>
                Invite Clubs
              </ActionButton>
            </Box>
          </TouchFriendlyTooltip>
        )}
        <ActionButton
          variant="outline"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </ActionButton>
      </PageHeader>

      <Card noPadding>
        <DataTable
          columns={columns}
          data={subOrgs}
          loading={loading}
          actions={renderActions}
          emptyMessage="No clubs found"
          mobileTitleColumn="name"
          mobileHiddenColumns={["id"]}
        />
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, subOrg: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 2 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Club Details
          </Typography>
          <IconButton
            onClick={() => setViewDialog({ open: false, subOrg: null })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          {viewDialog.subOrg && (
            <Grid container spacing={3}>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Avatar
                  src={viewDialog.subOrg.logo_url || ""}
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                    fontSize: "2rem",
                  }}
                >
                  {viewDialog.subOrg.name?.[0]?.toUpperCase() || "O"}
                </Avatar>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                  Club Name
                </Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
                  {viewDialog.subOrg.name || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                  Club Type
                </Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c" }}>
                  {formatClubType(viewDialog.subOrg.school_type)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                  Email
                </Typography>
                <Typography sx={{ fontSize: "1rem", color: isDark ? "#cbd5e1" : "#4a5568" }}>
                  {viewDialog.subOrg.user?.email || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                  Status
                </Typography>
                <StatusBadge status={viewDialog.subOrg.status || "approved"} />
              </Grid>
              {canControlEmailVisibility(viewDialog.subOrg) && (
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                  Email Visibility
                </Typography>
                <Chip
                  label={viewDialog.subOrg.allow_email_visibility ? "Enabled" : "Disabled"}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    backgroundColor: viewDialog.subOrg.allow_email_visibility
                      ? isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)"
                      : isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.15)",
                    color: viewDialog.subOrg.allow_email_visibility
                      ? isDark ? "#4ade80" : "#22c55e"
                      : isDark ? "#f87171" : "#ef4444",
                  }}
                />
                <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                  {viewDialog.subOrg.allow_email_visibility
                    ? "Club can view and export member emails"
                    : "Club cannot view or export member emails"}
                </Typography>
              </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setViewDialog({ open: false, subOrg: null })}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, subOrg: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Edit Club
          </Typography>
          <IconButton
            onClick={() => setEditDialog({ open: false, subOrg: null })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Avatar
                src={editForm.logo_url || ""}
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                  fontSize: "1.5rem",
                }}
              >
                {editForm.name[0]?.toUpperCase() || "O"}
              </Avatar>
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingLogo}
              sx={{
                borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#e2e8f0",
                color: isDark ? "#ffffff" : "#1a202c",
                textTransform: "none",
              }}
            >
              {uploadingLogo ? "Uploading..." : "Upload Logo"}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleLogoUpload}
              />
            </Button>
            <TextField
              label="Club Name"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDark ? "#242424" : "#f7fafc",
                },
              }}
            />
            <TextField
              select
              label="Club Type"
              value={editForm.school_type}
              onChange={(e) => setEditForm(prev => ({ ...prev, school_type: e.target.value }))}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: isDark ? "#242424" : "#f7fafc",
                },
              }}
            >
              <MenuItem value="School">School</MenuItem>
              <MenuItem value="Club">Club</MenuItem>
              <MenuItem value="Academy">Academy</MenuItem>
              <MenuItem value="Organisation">Club</MenuItem>
            </TextField>
            {canControlEmailVisibility(editDialog.subOrg) && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.allow_email_visibility}
                      onChange={(e) => setEditForm(prev => ({ ...prev, allow_email_visibility: e.target.checked }))}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: isDark ? "#4ade80" : "#22c55e",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: isDark ? "#4ade80" : "#22c55e",
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                        Email Visibility
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                        When enabled, this club can view and export member emails
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setEditDialog({ open: false, subOrg: null })}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <ActionButton
            variant="primary"
            onClick={handleUpdate}
            disabled={!editForm.name || !editForm.school_type}
          >
            Update
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Club?"
        message={
          <Box>
            <Typography sx={{ mb: 2, color: isDark ? "#cbd5e1" : "#4a5568" }}>
              Are you sure you want to remove the connection to "{deleteDialog.subOrg?.name}"?
            </Typography>
            <Typography sx={{ mb: 2, fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#718096" }}>
              This only severs your club partner's link. The club and any other club partners it belongs to are unaffected. The club immediately loses access to your alerts and communications.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteDialog.deleteUser}
                  onChange={(e) => setDeleteDialog(prev => ({ ...prev, deleteUser: e.target.checked }))}
                  sx={{
                    color: isDark ? "#ef4444" : "#dc2626",
                    "&.Mui-checked": {
                      color: isDark ? "#ef4444" : "#dc2626",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#cbd5e1" : "#4a5568" }}>
                  Also delete the user account
                </Typography>
              }
            />
            {deleteDialog.deleteUser && (
              <Typography sx={{ mt: 1, fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#718096" }}>
                The account is preserved if the club is still linked to another club partner or has its own active subscription.
              </Typography>
            )}
          </Box>
        }
        type="error"
        confirmText={deleteDialog.deleteUser ? "Remove & Delete User" : "Remove Connection"}
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, subOrg: null, deleteUser: false })}
      />

      {/* Members Dialog */}
      <Dialog
        open={membersDialog.open}
        onClose={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Members - {membersDialog.subOrgName}
          </Typography>
          <IconButton
            onClick={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: { xs: 2, sm: 3 }, maxHeight: "70vh", overflowY: "auto" }}>
          {membersDialog.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>Loading members...</Typography>
            </Box>
          ) : membersDialog.members.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No members found</Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {membersDialog.members.map((member, index) => (
                <Box
                  key={member.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    mb: 1,
                    borderRadius: "8px",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                    border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                  }}
                >
                  <Avatar
                    src={member.profile_image || ""}
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                      color: isDark ? "#818cf8" : "#1976d2",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {member.first_name?.[0] || ""}
                    {member.last_name?.[0] || ""}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                      {member.first_name || ""} {member.last_name || ""}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                      {member.email || "-"}
                    </Typography>
                    {member.role && (
                      <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#718096", mt: 0.5 }}>
                        Role: {member.role}
                      </Typography>
                    )}
                  </Box>
                  {member.status && (
                    <StatusBadge status={member.status} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setMembersDialog({ open: false, subOrgId: null, subOrgName: "", members: [], loading: false })}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog - for table display */}
      <Dialog
        open={usersDialog.open}
        onClose={() => setUsersDialog({ open: false, org: null, users: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
            Members - {usersDialog.org ? (usersDialog.org.name || "") : ""}
          </Typography>
          <IconButton
            onClick={() => setUsersDialog({ open: false, org: null, users: [] })}
            sx={{ color: isDark ? "#cbd5e1" : "#718096" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: { xs: 2, sm: 3 }, maxHeight: isMobile ? "none" : "70vh", overflowY: "auto" }}>
          {usersDialog.users.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>No members found</Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {usersDialog.users.map((user, index) => {
                const profileImage = user.profile_image || user.user?.profile_image || "";
                const firstName = user.first_name || user.name || "";
                const lastName = user.last_name || "";
                const fullName = `${firstName} ${lastName}`.trim() || "Unknown User";
                const userEmail = user.email || user.user?.email || "";
                const userRole = user.role || "";
                const userStatus = user.status || user.user?.status || "";
                const userSport = user.sport?.name || user.sport_name || "";
                const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "U";
                
                return (
                  <Box
                    key={user.id || user.user_id || index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      mb: 1,
                      borderRadius: "8px",
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f7fafc",
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
                    }}
                  >
                    <Avatar
                      src={profileImage}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                        color: isDark ? "#818cf8" : "#1976d2",
                        fontSize: "1.25rem",
                        fontWeight: 600,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                        {fullName}
                      </Typography>
                      {userEmail && (
                        <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                          {userEmail}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                        {userRole && (
                          <Chip
                            label={userRole}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              backgroundColor: isDark ? "rgba(99, 102, 241, 0.2)" : "rgba(25, 118, 210, 0.15)",
                              color: isDark ? "#818cf8" : "#1976d2",
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                        )}
                        {userSport && (
                          <Chip
                            label={userSport}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              backgroundColor: isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(34, 197, 94, 0.15)",
                              color: isDark ? "#4ade80" : "#22c55e",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {userStatus && (
                      <StatusBadge status={userStatus} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setUsersDialog({ open: false, org: null, users: [] })}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog
        open={openSendInvite}
        onClose={closeSendInvite}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c" }}>
              Invite Clubs
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#718096", mt: 0.25 }}>
              A unique invite code is generated and emailed to each club.
            </Typography>
          </Box>
          <IconButton onClick={closeSendInvite} sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {inviteClubs.map((club, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                  <TextField
                    label="Club Name"
                    value={club.name}
                    onChange={(e) => handleInviteClubChange(index, "name", e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: isDark ? "#242424" : "#f7fafc",
                      },
                    }}
                  />
                  <TextField
                    label="Club Email"
                    type="email"
                    value={club.email}
                    onChange={(e) => handleInviteClubChange(index, "email", e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: isDark ? "#242424" : "#f7fafc",
                      },
                    }}
                  />
                </Box>
                {inviteClubs.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveInviteClub(index)}
                    size="small"
                    sx={{ mt: 0.5, color: isDark ? "#fca5a5" : "#b91c1c" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddInviteClub}
            size="small"
            sx={{ mt: 1.5, color: isDark ? "#93c5fd" : "#1d4ed8", textTransform: "none", pl: 0 }}
          >
            Add another club
          </Button>
          <Divider sx={{ mt: 2, borderColor: isDark ? "rgba(255,255,255,0.07)" : "#f0f0f0" }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0" }}>
          <Button onClick={closeSendInvite} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <ActionButton variant="primary" onClick={handleSendInvite} disabled={sendingInvite}>
            {sendingInvite ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : inviteClubs.length > 1 ? (
              `Send ${inviteClubs.filter((c) => c.name.trim() && c.email.trim()).length || ""} Invites`
            ) : (
              "Send Invite"
            )}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubOrganizations;
